/**
 * Minimal Server-Sent Events reader over a `fetch` response body.
 *
 * We deliberately do NOT use the native `EventSource`: it can't send an
 * `Authorization` header, and the Hawk API authenticates with a bearer token.
 * Reading the stream via `fetch` lets the token ride in the header (see
 * `useApiFetch`), at the cost of parsing the wire format ourselves — which is
 * small: events are separated by a blank line, and each line is a
 * `field: value` pair (`:`-prefixed lines are comments / keepalives).
 */
export interface SseEvent {
  event: string;
  data: string;
}

function parseFrame(frame: string): SseEvent {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line === '' || line.startsWith(':')) continue; // comment / keepalive
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // A single leading space after the colon is part of the format, not data.
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  return { event, data: dataLines.join('\n') };
}

/**
 * Yields parsed SSE events from a response body until the stream ends or the
 * signal aborts. Comment-only frames (keepalives) are skipped.
 *
 * The SSE spec allows CRLF, LF, or CR line endings (a proxy may rewrite them),
 * so the buffer is normalized to LF before frames are split. A trailing CR is
 * held back: it may be the first half of a CRLF split across chunks.
 */
export async function* readSseStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal
): AsyncGenerator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let holdback = '';
      if (buffer.endsWith('\r')) {
        holdback = '\r';
        buffer = buffer.slice(0, -1);
      }
      buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseFrame(frame);
        if (parsed.data !== '') yield parsed;
      }
      buffer += holdback;
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
}
